import { useFormContext } from 'react-hook-form';

import { EAssetType, EPosition } from 'constants/enums';
import { z } from 'zod';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { cn } from 'components/lib/utils';
import Switch from 'components/Switch/Switch';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import UploadImage from 'components/UploadImage/UploadImage';

import { FormsSeparator } from '../Sidebar/Main/Components/FormMenuSeparator';
import { createColorSetValueHandler } from '../utils/blockForms.utils';
import { templateStudioSchema } from '../validation-schema';

const positionOptions = Object.values(EPosition).map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1)
}));

export const ExternalButtonsForm: React.FC = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors, touchedFields }
  } = useFormContext<z.infer<typeof templateStudioSchema>>();

  const isAddToHomeScreenEnabled: boolean = watch(
    'externalButtons.addToHomeScreenButton.active'
  );

  const isBackToGameEnabled: boolean = watch(
    'externalButtons.backToGameButton.active'
  );

  return (
    <div className="flex flex-col gap-5 text-md text-base-foreground mb-2">
      <div className="flex flex-col text-md font-semibold text-base-foreground mt-4">
        External Buttons Position
      </div>
      <FormField
        control={control}
        name="externalButtons.verticalOffset"
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <Input
              label="Vertical Offset (%)"
              name="externalButtons.verticalOffset"
              type="number"
              placeholder="0"
              value={value}
              min={0}
              max={100}
              onChange={(e) => onChange(Number(e.target.value))}
              infoTooltipContent="Adjust vertical position for external buttons"
              infoTooltipSide="top"
            />
          </FormItem>
        )}
      />

      <div className="flex flex-col gap-0 mb-4">
        <div className="flex flex-col text-md font-semibold text-base-foreground mt-4">
          Add To Home Screen
        </div>
        <div className="flex flex-col text-s text-base-mutedForeground mb-2">
          Players can access the store without needing to open a browser.
        </div>
      </div>
      <FormField
        control={control}
        name="externalButtons.addToHomeScreenButton.active"
        render={({ field: { value, onChange } }) => (
          <Switch
            key={value ? 'on' : 'off'}
            label="Activate this feature in your store"
            textAlignment="right"
            defaultChecked={value}
            checked={value}
            onCheckedChange={onChange}
          />
        )}
      />
      <div
        className={cn(
          'flex flex-col gap-5',
          !isAddToHomeScreenEnabled
            ? 'pointer-events-none opacity-50'
            : undefined
        )}
      >
        <FormField
          control={control}
          name="externalButtons.addToHomeScreenButton.shortcutName"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <Input
                label="Store Title"
                name="externalButtons.addToHomeScreenButton.shortcutName"
                type="text"
                value={value}
                isRequired={true}
                onChange={onChange}
                disabled={!isAddToHomeScreenEnabled}
              />
            </FormItem>
          )}
        />
        <FormField
          name="externalButtons.addToHomeScreenButton.borderColor"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <div className="flex flex-col items-start gap-2">
                <label className="font-medium text-base-foreground text-sm">
                  Border and Text Color
                </label>
                <FormControl className="w-full">
                  <div className="[&_.MuiInputAdornment-root]:mr-0 [&_.MuiTextField-root]:!w-[100%] [&_.MuiInputBase-input]:!pl-0">
                    <AcGradientInput
                      name="externalButtons.addToHomeScreenButton.borderColor"
                      defaultValue={
                        value || {
                          colorOne: '#ffffff'
                        }
                      }
                      setValue={createColorSetValueHandler(
                        'externalButtons.addToHomeScreenButton.borderColor',
                        () =>
                          watch(
                            'externalButtons.addToHomeScreenButton.borderColor'
                          ),
                        setValue as (name: string, value: any) => void,
                        onChange
                      )}
                      onlySolid={true}
                      circular={true}
                      imgWidth="16px"
                      imgHeight="16px"
                      error={
                        touchedFields?.externalButtons?.addToHomeScreenButton
                          ?.borderColor &&
                        Boolean(
                          errors.externalButtons?.addToHomeScreenButton
                            ?.borderColor
                        )
                      }
                      helperText={
                        touchedFields?.externalButtons?.addToHomeScreenButton
                          ?.borderColor
                          ? errors.externalButtons?.addToHomeScreenButton?.borderColor?.toString()
                          : ''
                      }
                      disabled={!isAddToHomeScreenEnabled}
                    />
                  </div>
                </FormControl>
              </div>

              <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
            </FormItem>
          )}
        />
        <FormField
          name="externalButtons.addToHomeScreenButton.backgroundColor"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <div className="flex flex-col items-start gap-2">
                <label className="font-medium text-base-foreground text-sm">
                  Background Color
                </label>
                <FormControl className="w-full">
                  <div className="[&_.MuiInputAdornment-root]:mr-0 [&_.MuiTextField-root]:!w-[100%] [&_.MuiInputBase-input]:!pl-0">
                    <AcGradientInput
                      name="externalButtons.addToHomeScreenButton.backgroundColor"
                      defaultValue={
                        value || {
                          colorOne: '#522AD8',
                          colorTwo: '#522AD8'
                        }
                      }
                      setValue={createColorSetValueHandler(
                        'externalButtons.addToHomeScreenButton.backgroundColor',
                        () =>
                          watch(
                            'externalButtons.addToHomeScreenButton.backgroundColor'
                          ),
                        setValue as (name: string, value: any) => void,
                        onChange
                      )}
                      onlySolid={false}
                      circular={true}
                      imgWidth="16px"
                      imgHeight="16px"
                      error={
                        touchedFields?.externalButtons?.addToHomeScreenButton
                          ?.backgroundColor &&
                        Boolean(
                          errors.externalButtons?.addToHomeScreenButton
                            ?.backgroundColor
                        )
                      }
                      helperText={
                        touchedFields?.externalButtons?.addToHomeScreenButton
                          ?.backgroundColor
                          ? errors.externalButtons?.addToHomeScreenButton?.backgroundColor?.toString()
                          : ''
                      }
                      disabled={!isAddToHomeScreenEnabled}
                    />
                  </div>
                </FormControl>
              </div>
              <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
            </FormItem>
          )}
        />
        <FormField
          name="externalButtons.addToHomeScreenButton.buttonImage"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <div data-testid="BrandKit-ButtonImageUpload">
                  <UploadImage
                    className="w-full !flex flex-row px-5 mb-4 mt-4"
                    title="Button Image"
                    buttonText="Upload"
                    data-testid="BrandKit-ButtonImageUpload"
                    selectedImageUrl={value}
                    forceType={EAssetType.ADD_TO_HOME_BUTTON_IMAGE}
                    onUpload={(url: string) => {
                      onChange(url);
                    }}
                    labelClassName="text-xs"
                    TooltipContent={() => (
                      <div>
                        <p>
                          This image appears on the Add To Home Screen button in
                          your store
                        </p>
                      </div>
                    )}
                  />
                </div>
              </FormControl>
              <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
            </FormItem>
          )}
        />
        <FormField
          name="externalButtons.addToHomeScreenButton.iconImage"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <div data-testid="BrandKit-IconImageUpload">
                  <UploadImage
                    forceType={EAssetType.ADD_TO_HOME_ICON_IMAGE}
                    isRequired
                    className="w-full !flex flex-row px-5 mb-5"
                    title="Icon Image"
                    buttonText="Upload"
                    data-testid="BrandKit-IconImageUpload"
                    selectedImageUrl={value}
                    onUpload={(url: string) => {
                      onChange(url);
                    }}
                    labelClassName="text-xs"
                    TooltipContent={() => (
                      <div>
                        <p>
                          This icon appears on the player's device home screen
                        </p>
                      </div>
                    )}
                  />
                </div>
              </FormControl>
              <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
            </FormItem>
          )}
        />
      </div>

      <FormsSeparator />

      <div className="flex flex-col gap-0 mb-4">
        <div className="flex flex-col text-md font-semibold text-base-foreground mt-4">
          Back To Game
        </div>
        <div className="flex flex-col text-md text-base-mutedForeground mb-2 font-normal">
          Players quickly return to your game from the web store
        </div>
      </div>

      <div className="flex flex-col gap-9 mb-4">
        <FormField
          control={control}
          name="externalButtons.backToGameButton.active"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <Switch
                  key={value ? 'on' : 'off'}
                  label="Activate this feature in your store"
                  textAlignment="right"
                  defaultChecked={value}
                  checked={value}
                  onCheckedChange={onChange}
                />
              </FormControl>
              <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
            </FormItem>
          )}
        />
        <div
          className={cn(
            'flex flex-col gap-9',
            !isBackToGameEnabled ? 'pointer-events-none opacity-50' : undefined
          )}
        >
          <FormField
            control={control}
            name="externalButtons.backToGameButton.position"
            render={({ field: { onChange } }) => (
              <FormItem>
                <div className="flex flex-col items-start gap-2">
                  <label className="font-medium text-base-foreground text-sm">
                    Buttons Side Position
                  </label>
                  <FormControl>
                    <InputSelectDropdown
                      data-testid="BrandKit-BackToGameButtonPosition"
                      buttonDataTestId="BrandKit-BackToGameButtonPositionSelect"
                      listDataTestId="BrandKit-BackToGameButtonPositionSelectList"
                      defaultSelected={positionOptions.find(
                        (option) =>
                          option.value ===
                          watch('externalButtons.backToGameButton.position')
                      )}
                      options={positionOptions}
                      onOptionChange={onChange}
                      disabled={!isBackToGameEnabled}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
                </div>
              </FormItem>
            )}
          />
          <FormField
            name="externalButtons.backToGameButton.buttonImage"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <div data-testid="BrandKit-BackToGameButtonImageUpload">
                    <UploadImage
                      isRequired
                      className="w-full !flex flex-row px-5"
                      title="Button Image"
                      buttonText="Upload"
                      data-testid="BrandKit-BackToGameButtonImageUpload"
                      selectedImageUrl={value}
                      forceType={EAssetType.BACK_TO_GAME_BUTTON_IMAGE}
                      onUpload={(url: string) => {
                        onChange(url);
                      }}
                      labelClassName="text-xs"
                    />
                  </div>
                </FormControl>
                <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};
